import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useNode } from '../contexts/NodeContext';
import { useTheme } from '../contexts/ThemeContext';
import { login as apiLogin, register as apiRegister } from '../services/api.service';
import { resolveScopeSwitch } from '../constants/scope';
import trinetraLogoLight from '../assets/logos/trinetra-logo-light.png';
import trinetraLogoWhite from '../assets/logos/trinetra-logo-white.png';
import samikshaBrain from '../assets/logos/samiksha-brain-icon.png';

export default function Login() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { selectNode } = useNode();
  const { theme } = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let result;
      if (isRegister) {
        result = await apiRegister(email, password, name);
      } else {
        result = await apiLogin(email, password);
      }
      setUser({ ...result.user, permissions: result.permissions }, result.token);

      // A login never inherits what THIS BROWSER was last looking at: that site may belong to
      // the previous user, or be one this operator cannot reach, and either way it drops them
      // into a site's menu with no idea why. So the browser's stored scope is discarded here.
      //
      // The account's own default site is the exception, and the distinction is the point — it
      // is not a leftover, it is a choice this operator made and stored against their login. So
      // it is honoured, and only the estate dashboard is used when they have not set one.
      const favourite: string | null = result.user?.default_node_id || null;
      selectNode(favourite);
      // Land the page in the same scope as the menu. Sending a site-scoped session to the
      // all-sites Dashboard would show one scope's page beside the other's sidebar, which is
      // the exact ambiguity ScopeSync exists to prevent.
      navigate(favourite ? resolveScopeSwitch('/', false) || '/' : '/');
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background bg-grid-pattern">
      <div className="w-full max-w-md px-4">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img
            src={theme === 'dark' ? trinetraLogoWhite : trinetraLogoLight}
            alt="Trinetra"
            className="h-10 mb-3"
          />
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <img src={samikshaBrain} alt="" className="h-4 w-4 opacity-60" />
            <span>Cloud Management Console</span>
          </div>
        </div>

        {/* Card */}
        <div className="bg-card border border-border rounded-xl shadow-card p-8">
          <h1 className="text-xl font-semibold text-foreground mb-1 text-center">
            {isRegister ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="text-muted-foreground text-sm text-center mb-6">
            {isRegister ? 'Set up your cloud account' : 'Sign in to manage your sites'}
          </p>

          {error && (
            <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-2.5 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-input text-foreground px-4 py-2.5 rounded-lg border border-border focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none transition-colors placeholder:text-muted-foreground"
                  placeholder="Your name"
                  required
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-input text-foreground px-4 py-2.5 rounded-lg border border-border focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none transition-colors placeholder:text-muted-foreground"
                placeholder="admin@example.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-input text-foreground pl-4 pr-11 py-2.5 rounded-lg border border-border focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none transition-colors placeholder:text-muted-foreground"
                  placeholder="••••••••"
                  autoComplete={isRegister ? 'new-password' : 'current-password'}
                  required
                  minLength={6}
                />
                {/* Same show/hide control as the integrations secret field (ConnectorGrid). */}
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand hover:bg-brand-dark text-white py-2.5 rounded-lg font-medium disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Please wait...
                </span>
              ) : isRegister ? 'Create Account' : 'Sign In'}
            </button>
          </form>

          <div className="mt-5 pt-4 border-t border-border text-center">
            <p className="text-muted-foreground text-sm">
              {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button
                onClick={() => { setIsRegister(!isRegister); setError(''); }}
                className="text-brand hover:text-brand-dark font-medium transition-colors"
              >
                {isRegister ? 'Sign In' : 'Register'}
              </button>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-muted-foreground text-xs mt-6">
          © {new Date().getFullYear()} Samiksha.ai — Trinetra TRINETRA Cloud
        </p>
      </div>
    </div>
  );
}
